import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import { PageHeader, Loading, ScoreChip, ScoreBar } from "@/components/common";
import { fmtUsd } from "@/lib/format";
import { Sparkles } from "lucide-react";

const STAGE_COLOR = {
  Emerging: "text-cyan-400 border-cyan-800/50 bg-cyan-950/30",
  Growing: "text-emerald-400 border-emerald-800/50 bg-emerald-950/30",
  Trending: "text-amber-400 border-amber-800/50 bg-amber-950/30",
  Saturated: "text-orange-400 border-orange-800/50 bg-orange-950/30",
  Declining: "text-rose-400 border-rose-800/50 bg-rose-950/30",
};

export default function NarrativeRadar() {
  const { data, loading } = useFetch("/narratives", []);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();

  const openNarrative = async (id) => {
    setSelected(id);
    setDetail(null);
    const r = await http.get(`/narratives/${id}`);
    setDetail(r.data);
  };

  if (loading || !data) return <Loading />;

  return (
    <div className="fade-in">
      <PageHeader
        icon={Sparkles}
        title="Narrative Radar"
        subtitle="AI-assisted narrative discovery across the Solana ecosystem. Distinguishes genuinely emerging narratives from saturated ones."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {data.data.map((n) => (
          <button
            key={n.id}
            data-testid={`narrative-${n.id}`}
            onClick={() => openNarrative(n.id)}
            className={`pnut-card p-4 text-left ${selected === n.id ? "border-amber-600/50" : ""}`}
          >
            <div className="flex items-start justify-between">
              <h3 className="font-display font-semibold text-slate-100">{n.name}</h3>
              <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${STAGE_COLOR[n.stage]}`}>
                {n.stage}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-[11px] text-slate-500">{n.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <ScoreChip type="momentum" value={n.narrative_score} confidence={n.confidence} />
              <span className="font-mono text-[10px] text-slate-500">{n.tokens_count} tokens</span>
            </div>
            <div className="mt-2">
              <ScoreBar label="Mention accel" value={n.breakdown.mention_acceleration} />
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          <div className="pnut-card p-5 lg:col-span-1">
            {!detail ? (
              <Loading label="Loading narrative…" />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-bold text-slate-50">{detail.data.name}</h3>
                  <ScoreChip type="momentum" value={detail.data.narrative_score} confidence={detail.data.confidence} />
                </div>
                <p className="mt-1 text-xs text-slate-400">{detail.data.description}</p>
                <div className="mt-4 space-y-2.5">
                  {Object.entries(detail.data.breakdown).map(([k, v]) => (
                    <ScoreBar key={k} label={k.replace(/_/g, " ")} value={v} />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="pnut-card overflow-hidden">
              <div className="border-b border-slate-800 px-4 py-2.5 font-display text-sm font-semibold text-slate-200">
                Tokens in this narrative
              </div>
              <div className="max-h-[50vh] overflow-auto">
                <table className="pnut-table w-full">
                  <thead>
                    <tr><th>Token</th><th>Market Cap</th><th>Mom</th><th>Org</th><th>Risk</th></tr>
                  </thead>
                  <tbody>
                    {detail?.tokens?.map((t) => (
                      <tr key={t.id} onClick={() => nav(`/token/${t.id}`)}>
                        <td>
                          <div className="flex items-center gap-2">
                            <img src={t.logo} alt="" className="h-5 w-5 rounded-full" />
                            <span className="font-semibold text-slate-200">${t.symbol}</span>
                          </div>
                        </td>
                        <td className="font-mono text-slate-300">{fmtUsd(t.market_cap)}</td>
                        <td><ScoreChip type="momentum" value={t.momentum_score} /></td>
                        <td><ScoreChip type="organic" value={t.organic_score} /></td>
                        <td><ScoreChip type="risk" value={t.risk_score} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
